import { createMiddleware } from "@tanstack/react-start";
import { getAuth } from "@clerk/tanstack-react-start/server";
import { getWebRequest } from "@tanstack/react-start/server";
import type { UserSelect } from "~/lib/schemas";

export async function getUser(clerkUserId: string) {
  const { getUserByClerkId, createEmptyUser, updateUser } = await import(
    "~/lib/functions/user"
  );

  let user = await getUserByClerkId({ data: { clerkUserId } });
  if (!user) {
    console.log(
      "User not found in database for Clerk ID:",
      clerkUserId,
      "- creating user automatically",
    );
    try {
      // Auto-create user if they exist in Clerk but not in our database
      const newUser = await createEmptyUser({ data: {} });
      await updateUser({
        data: {
          userId: newUser.id,
          user: { clerkUserId: clerkUserId },
        },
      });

      // Fetch the newly created user
      user = await getUserByClerkId({ data: { clerkUserId } });
      console.log("✅ Auto-created user for Clerk ID:", clerkUserId);
    } catch (createError) {
      console.error("Failed to auto-create user:", createError);
      throw new Error("Failed to create user in database");
    }
  }

  return user;
}

export const authMiddleware = createMiddleware({ type: "function" }).server<{
  user: UserSelect;
}>(async ({ next, context: _context }) => {
  try {
    const webRequest = getWebRequest();
    if (!webRequest) {
      throw new Error("No web request available");
    }

    const auth = await getAuth(webRequest);
    if (!auth?.userId) {
      throw new Error("Authentication required");
    }

    const user = await getUser(auth.userId);

    if (!user) {
      // This should not happen after auto-creation, but just in case
      throw new Error("User creation failed");
    }

    return next({
      context: {
        user,
      },
    });
  } catch (error) {
    // Server function middleware should throw errors, not redirects
    // Redirects are handled at the route level in beforeLoad
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Authentication failed");
  }
});
